import "./StockModal.scss";
import { useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import FormField from "../../../../components/formField/FormField";
import useStockCsv from "../../../../hooks/useStockCsv";
import StockModal from "./StockModal";

export default function ImportExportStockModal({
  open,
  onClose,
  stockCategories = [],
  stockItems = [],
  venueId,
  onImportSuccess,
}) {
  const { t } = useTranslation("stockManagement");
  const userStore = useSelector((state) => state.userReducer);
  const [movementsStartDate, setMovementsStartDate] = useState(() =>
    dayjs().subtract(7, "day").startOf("day").toISOString(),
  );
  const [movementsEndDate, setMovementsEndDate] = useState(() =>
    dayjs().endOf("day").toISOString(),
  );
  const [selectedMovementStockItemId, setSelectedMovementStockItemId] = useState("");
  const movementStockItemOptions = useMemo(
    () => [
      { value: "", label: t("stock_overview_status_option_all") },
      ...stockItems.map((item) => ({ value: item.id, label: item.name })),
    ],
    [stockItems, t],
  );

  const {
    csvLoader, restockReport, restockRetryCount, retryRestockImport,
    importFileInputRef,
    inventoryUpdateFileInputRef,
    restockUpdateFileInputRef,
    handleProductItemsCsvExport,
    handleProductItemsCsvImport,
    handleInventoryUpdateCsvImport,
    handleRestockUpdateCsvImport,
    handleMovementsCsvExport,
  } = useStockCsv({
    stockItems,
    stockCategories,
    venueId,
    createdBy: userStore?.users?.uid,
    movementsStartDate,
    movementsEndDate,
  });

  return (
    <StockModal
      open={open}
      onClose={onClose}
      isLoading={csvLoader}
      title={t("stock_overview_import_export_modal_title")}
    >
      <div className="stock-import-export-modal">
        <input
          ref={importFileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(event) => handleProductItemsCsvImport(event, onImportSuccess)}
          disabled={csvLoader}
          style={{ display: "none" }}
        />
        <input
          ref={inventoryUpdateFileInputRef}
          type="file"
          accept=".csv,.numbers,text/csv,application/vnd.apple.numbers,application/x-iwork-numbers-sffnumbers"
          onChange={(event) => handleInventoryUpdateCsvImport(event, onImportSuccess)}
          disabled={csvLoader}
          style={{ display: "none" }}
        />
        <input
          ref={restockUpdateFileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(event) => handleRestockUpdateCsvImport(event, onImportSuccess)}
          disabled={csvLoader}
          style={{ display: "none" }}
        />

        <div className="stock-import-export-modal-dates">
          <FormField
            type="date"
            label={t("stock_overview_movements_date_from")}
            value={movementsStartDate}
            onChange={setMovementsStartDate}
          />
          <FormField
            type="date"
            label={t("stock_overview_movements_date_to")}
            value={movementsEndDate}
            onChange={setMovementsEndDate}
          />
          <FormField
            type="dropdown"
            label={t("stock_overview_movements_stock_item")}
            value={selectedMovementStockItemId}
            dropDownValues={movementStockItemOptions}
            onChange={setSelectedMovementStockItemId}
          />
        </div>

        {restockReport && <div role="status">
          <p>{t('stock_restock_report', { count: restockReport.updatedCount, ignored: restockReport.ignored })}</p>
          {!!restockReport.invalidLines?.length && <p>{t('stock_restock_invalid_lines', { lines: restockReport.invalidLines.join(', ') })}</p>}
          {!!restockReport.failedLines?.length && <p>{t('stock_restock_failed_lines', { lines: restockReport.failedLines.join(', ') })}</p>}
          {!!restockRetryCount && <button className="stock-import-export-modal-btn" disabled={csvLoader}
            onClick={() => retryRestockImport(onImportSuccess)}>{t('stock_restock_retry')}</button>}
        </div>}
        <p>{t('stock_restock_delta_help')}</p>
        <div className="stock-import-export-modal-actions">
          <button
            className="stock-import-export-modal-btn"
            disabled={csvLoader}
            onClick={() => {
              if (!csvLoader) handleMovementsCsvExport(selectedMovementStockItemId || null);
            }}
          >
            {csvLoader ? <Spinner size="sm" /> : t("stock_overview_export_movements_csv")}
          </button>
          <button
            className="stock-import-export-modal-btn"
            disabled={csvLoader}
            onClick={() => {
              if (!csvLoader) importFileInputRef.current?.click();
            }}
          >
            {csvLoader ? <Spinner size="sm" /> : t("stock_overview_import_items_csv")}
          </button>
          <button
            className="stock-import-export-modal-btn"
            disabled={csvLoader}
            onClick={() => {
              if (!csvLoader) inventoryUpdateFileInputRef.current?.click();
            }}
          >
            {csvLoader ? <Spinner size="sm" /> : t("stock_overview_import_inventory_update_csv")}
          </button>
          <button
            className="stock-import-export-modal-btn"
            disabled={csvLoader}
            onClick={() => {
              if (!csvLoader && !restockRetryCount) restockUpdateFileInputRef.current?.click();
            }}
          >
            {csvLoader ? <Spinner size="sm" /> : t("stock_overview_import_restock_update_csv")}
          </button>
          <button
            className="stock-import-export-modal-btn"
            disabled={csvLoader}
            onClick={() => {
              if (!csvLoader) handleProductItemsCsvExport();
            }}
          >
            {csvLoader ? <Spinner size="sm" /> : t("stock_overview_export_items_csv")}
          </button>
        </div>
      </div>
    </StockModal>
  );
}
